class ProductDocuments {
  constructor(root) {
    this.root = root;
    this.shop = root.dataset.shop;
    this.token = (root.dataset.token || '').trim();
    this.apiVersion = (root.dataset.apiVersion || '2025-01').trim();
    this.metaobjectType = root.dataset.metaobjectType || 'documents';
    this.productId = root.dataset.productId || '';
    this.productGid = root.dataset.productGid || '';
    this.productHandle = root.dataset.productHandle || '';
    this.emptyMessage = root.dataset.emptyMessage || 'No documents available';
    this.linkLabel = (root.dataset.linkLabel || 'View document').trim() || 'View document';
    this.showWhenEmpty = root.dataset.showWhenEmpty === 'true';
    this.designMode = root.dataset.designMode === 'true';
    this.forceHidden = root.dataset.forceHidden === 'true';
    this.listEl = root.querySelector('[data-pd-list]');
    this.emptyEl = root.querySelector('[data-pd-empty]');
    this.cacheKey = `pd-docs:v3:${this.shop}:${this.metaobjectType}`;

    if (!this.shop || !this.token || !this.listEl) {
      this.renderEmpty();
      return;
    }

    this.init();
  }

  async init() {
    try {
      const documents = await this.getDocuments();
      const files = this.filesForProduct(documents);
      this.render(files);
    } catch (error) {
      console.error('[ProductDocuments]', error);
      this.renderEmpty();
    }
  }

  async getDocuments() {
    try {
      const cached = sessionStorage.getItem(this.cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch (_error) {
      // ignore cache errors
    }

    const documents = [];
    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const data = await this.queryDocuments(cursor);
      const connection = data?.metaobjects;
      if (!connection) break;

      connection.nodes.forEach((node) => {
        const parsed = this.parseDocument(node);
        if (parsed) documents.push(parsed);
      });

      hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      cursor = connection.pageInfo?.endCursor || null;
    }

    try {
      sessionStorage.setItem(this.cacheKey, JSON.stringify(documents));
    } catch (_error) {
      // ignore quota errors
    }

    return documents;
  }

  async queryDocuments(cursor) {
    const query = `
      query ProductDocuments($type: String!, $first: Int!, $after: String) {
        metaobjects(type: $type, first: $first, after: $after) {
          nodes {
            id
            handle
            file: field(key: "file") {
              references(first: 25) {
                nodes {
                  ... on GenericFile {
                    id
                    url
                  }
                  ... on MediaImage {
                    id
                    image { url }
                  }
                }
              }
            }
            products: field(key: "products") {
              references(first: 50) {
                nodes {
                  ... on Product {
                    id
                    handle
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    const response = await fetch(`https://${this.shop}/api/${this.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.token,
      },
      body: JSON.stringify({
        query,
        variables: {
          type: this.metaobjectType,
          first: 50,
          after: cursor,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API HTTP ${response.status}`);
    }

    const json = await response.json();
    if (json.errors?.length) {
      throw new Error(json.errors.map((item) => item.message).join('; '));
    }

    return json.data;
  }

  parseDocument(node) {
    if (!node) return null;

    const fileNodes = node.file?.references?.nodes || [];
    const files = fileNodes
      .map((fileRef) => {
        const url = fileRef?.url || fileRef?.image?.url || '';
        if (!url) return null;
        return { url };
      })
      .filter(Boolean);

    if (!files.length) return null;

    const products = (node.products?.references?.nodes || [])
      .filter((product) => product && (product.id || product.handle))
      .map((product) => ({
        id: product.id || '',
        handle: product.handle || '',
      }));

    return { files, products };
  }

  filesForProduct(documents) {
    const seen = new Set();
    const files = [];

    documents.forEach((doc) => {
      const matches = (doc.products || []).some((product) => {
        if (this.productGid && product.id === this.productGid) return true;
        if (this.productId && product.id.endsWith(`/${this.productId}`)) return true;
        if (this.productHandle && product.handle === this.productHandle) return true;
        return false;
      });

      if (!matches) return;

      doc.files.forEach((file) => {
        if (seen.has(file.url)) return;
        seen.add(file.url);
        files.push({
          url: file.url,
          label: this.linkLabel,
        });
      });
    });

    return files;
  }

  render(files) {
    if (this.forceHidden) {
      this.root.hidden = true;
      return;
    }

    if (!files.length) {
      this.renderEmpty();
      return;
    }

    this.root.hidden = false;
    if (this.emptyEl) this.emptyEl.hidden = true;
    this.listEl.hidden = false;
    this.listEl.innerHTML = files
      .map(
        (file) => `
        <li class="product-documents__item">
          <a
            class="product-documents__link"
            href="${this.escapeAttr(file.url)}"
            target="_blank"
            rel="noopener noreferrer"
          >${this.escapeHtml(file.label)}</a>
        </li>
      `
      )
      .join('');
  }

  renderEmpty() {
    if (this.listEl) {
      this.listEl.hidden = true;
      this.listEl.innerHTML = '';
    }

    if (this.forceHidden || (!this.showWhenEmpty && !this.designMode)) {
      this.root.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = true;
      return;
    }

    this.root.hidden = false;
    if (this.emptyEl) {
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = this.emptyMessage;
    }
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeAttr(value) {
    return this.escapeHtml(value);
  }
}

function initProductDocuments(scope = document) {
  scope.querySelectorAll('[data-product-documents]').forEach((root) => {
    if (root.dataset.pdInitialized === 'true') return;
    root.dataset.pdInitialized = 'true';
    new ProductDocuments(root);
  });
}

document.addEventListener('DOMContentLoaded', () => initProductDocuments());
document.addEventListener('shopify:section:load', (event) => initProductDocuments(event.target));